import { components } from 'api/goodtrust/swagger'
import { deleteUserSite, putUserSiteContact } from 'api/goodtrust/user/site'
import { Button } from 'components/button/Button'
import { ContactShareItem } from 'components/contact/contactShareForm/ContactShareForm'
import {
  ContactShareInvite,
  parseSharedInvitedContacts,
} from 'components/contact/contactShareInvite/ContactShareInvite'
import { Icon } from 'components/icon'
import { ConfirmModal } from 'components/modal/confirmModal/ConfirmModal'
import { useModal } from 'components/modal/Modal'
import { usePopperModal } from 'components/modal/PopperModal'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import {
  useCardStatusComplete,
  useCardStatusMissingTC,
  WizardGridCard,
} from 'components/wizard/wizardGridCard/WizardGridCard'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import { colors } from 'utils/colors'
import { formatUpdatedAgo } from 'utils/format'
import { assertFields } from 'utils/general'
import { useLoadingCallback } from 'utils/hooks'
import { getDigitalVaultURLs } from 'utils/url'

export function SiteCard(props: {
  site:
    | components['schemas']['UserSiteResponse']
    | components['schemas']['UserSiteListItemResponse']
  onDataChange?: () => void
  sharedWithMe?: boolean
  showType?: boolean
}) {
  const { t } = useTranslation('trusted_contact')
  const [handleShare, isSharing] = useLoadingCallback(
    async (contactShareData: ContactShareItem[]) => {
      try {
        assertFields(props.site, ['uuid'])
        const res = await putUserSiteContact(props.site.uuid, {
          share:
            contactShareData?.map((contact) => ({
              trusted_contact_uuid: contact.uuid,
              access_type: 'READ_ONLY',
              share_mode: contact.share_mode || 'NOW',
            })) ?? [],
        })
        if (!res.ok) throw Error()
        toastSuccess('site_shared')
        closeMenu()
        await props.onDataChange?.()
      } catch (err) {
        handleAndToastError(err, 'failed_to_share_site')
      }
    }
  )

  const [deleteModal, showDeleteModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        title={t('trusted_contact.site.delete_modal_confirm_title')}
        danger
        onConfirm={async () => {
          try {
            assertFields(props.site, ['uuid'])
            const res = await deleteUserSite(props.site.uuid)
            if (!res.ok) throw Error(res?.error?.toString?.())
            toastSuccess('site_deleted')
            close()
            props.onDataChange?.()
          } catch (err) {
            handleAndToastError(err, 'failed_to_delete_site')
          }
        }}
        close={close}
      />
    ) : null
  )

  const [inviteModal, showInviteModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <ContactShareInvite
          hideAccessType
          defaultValue={parseSharedInvitedContacts(props.site.trusted_contacts)}
          onSubmit={(data) => {
            handleShare(data)
            close()
          }}
        />
      ) : null,
    {
      disableFocusLock: true,
      modalContentCss: { overflowY: 'visible' },
      mobilePlacement: 'top',
    }
  )

  const {
    modal: menu,
    setReferenceElement,
    open: openMenu,
    close: closeMenu,
  } = usePopperModal(
    () => (
      <>
        <Button
          type="button"
          variant="text"
          onClick={() => void showInviteModal()}
          leftIcon={<Icon name="Share" />}
        >
          {t('trusted_contact.site.menu_share_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          onClick={() => void Router.push(urls.edit)}
          leftIcon={<Icon name="DocumentEdit" />}
        >
          {t('trusted_contact.site.menu_edit_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          tone="danger"
          onClick={showDeleteModal}
          leftIcon={<Icon name="Bin" />}
        >
          {t('trusted_contact.site.menu_delete_button')}
        </Button>
      </>
    ),
    { placement: 'bottom-end' }
  )

  const urls = getDigitalVaultURLs('site', props.site.uuid)
  const cardStatusMissingTC = useCardStatusMissingTC()
  const cardStatusComplete = useCardStatusComplete()

  return (
    <>
      {inviteModal}
      {deleteModal}
      {menu}
      <WizardGridCard
        ref={setReferenceElement}
        data-testid={`site-item-${props.site.uuid}`}
        entityType={props.showType ? 'Account' : null}
        image={
          props.site?.logo?.url ? (
            <img src={props.site.logo.url} alt={props.site.title ?? undefined} />
          ) : (
            <Icon name="Globe" color={colors.gray[500]} />
          )
        }
        title={props.site?.title}
        brief={props.site.modified ? formatUpdatedAgo(props.site.modified) : ''}
        isLoading={isSharing}
        openMenu={props.sharedWithMe ? undefined : openMenu}
        onClick={() => void Router.push(props.sharedWithMe ? urls.shared : urls.edit)}
        status={!props.site.trusted_contacts?.length ? cardStatusMissingTC : cardStatusComplete}
      />
    </>
  )
}
